import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, TextField, Button, Link } from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import api from './api';
import Header from './components/Header';
import { useConfirmBackLogout } from './common/useConfirmBackLogout';
import BackButton from './common/BackButton';
import { useNavigationHistory } from "./common/navigationHistory.js";
import { validateSelectedFiles } from "./common/fileUploadValidation.js";

const allowedNoticeAttachmentTypes = '.pdf,.jpg,.jpeg,.png,.webp,.txt,.xlsx,.docx,.pptx';

const CreateNotice = () => {
  const [noticeDate, setNoticeDate] = useState('');
  const [noticeTitle, setNoticeTitle] = useState('');
  const [noticeBody, setNoticeBody] = useState('');
  const [file, setFile] = useState(null);
  const [notices, setNotices] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const noticesPerPage = 5;
  const [noticeCategory, setNoticeCategory] = useState('お知らせ');
  const [visibilitySetting, setVisibilitySetting] = useState('全体');
  const [showFormMenu, setShowFormMenu] = useState(false);

  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('user');
    sessionStorage.clear(); // ← これ追加！
    navigate('/login/host');
  };

  const handleFileChange = (e) => {
    const validation = validateSelectedFiles(e.target.files);
    if (!validation.valid) {
      alert(validation.message);
      e.target.value = null;
      return;
    }
    setFile(validation.files[0] || null);
  };

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const res = await api.get(`/api/notices?skip=${(page - 1) * noticesPerPage}&limit=${noticesPerPage}`);
        setNotices(res.data.items);
        setTotal(res.data.total);
      } catch (err) {
        console.error('お知らせ一覧の取得に失敗:', err);
      }
    };
    fetchNotices();
  }, [page]);
  
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append('date', noticeDate);
    formData.append('title', noticeTitle);
    formData.append('body', noticeBody);
    formData.append('category', noticeCategory);
    formData.append('visibility_setting', visibilitySetting);
    if (file) formData.append('file', file);

    try {
      await api.post('/api/notices/create', formData);
      alert('お知らせを作成しました');
      setNoticeDate('');
      setNoticeTitle('');
      setNoticeBody('');
      setFile(null);
    } catch (err) {
      console.error('送信失敗:', err);
      alert('お知らせの作成に失敗しました');
    }
  };

  useConfirmBackLogout(navigate);
  useNavigationHistory();

  return (
    <div>
      <Header
        title="メッセナゴヤ お知らせ作成"
      />
      <Container sx={{ mt: 5 }}>
        <Box sx={{ display: 'flex' }}>
          {/* 左メニュー */}
          <Box sx={{ width: '20%', pr: 2, borderRight: '1px solid #ccc' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box className="menu-item" onClick={() => navigate('/host-dashboard')}>
                申請状況
              </Box>
              <Box
                className="menu-group"
                id="hover-block"
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
              >
                <Box className="menu-item reverse-triangle" onClick={() => setShowFormMenu(prev => !prev)}>
                  会社情報
                </Box>
                  {showFormMenu && (
                    <Box className="hover-menu">
                      <Typography
                        sx={{ color: '#0071bc', textAlign: 'center' }}
                        className='form-button'
                        onClick={() => navigate('/exhibitor-info')}
                      >
                        出展者
                      </Typography>
                      <Typography
                        sx={{ color: '#0071bc', textAlign: 'center' }}
                        className='form-button'
                        onClick={() => navigate('/corporate-info')}
                      >
                        協力会社
                      </Typography>
                    </Box>
                  )}
              </Box>
              <Box className="menu-item" onClick={() => navigate('/create-mail')}>
                メール作成
              </Box>
              <Box className="menu-item" onClick={() => navigate('/create-notice')}>
                お知らせ作成
              </Box>
              <Box className="menu-item" onClick={() => navigate('/form-history')}>
                再提出ログ確認
              </Box>
            </Box>
            <BackButton />
          </Box>

          {/* メインコンテンツ */}
          <Box sx={{ width: '80%', pl: 2 }}>
            <Typography variant="h6" gutterBottom className='title'>お知らせ作成</Typography>

            <TextField
              label="日付"
              type="datetime-local"
              fullWidth
              value={noticeDate}
              onChange={(e) => setNoticeDate(e.target.value)}
              sx={{ my: 2 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label="タイトル"
              fullWidth
              value={noticeTitle}
              onChange={(e) => setNoticeTitle(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle1" sx={{ mt: 2 }}>カテゴリー</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {['お知らせ', '重要', '緊急'].map((cat) => (
                <label key={cat}>
                  <input
                    type="radio"
                    value={cat}
                    checked={noticeCategory === cat}
                    onChange={(e) => setNoticeCategory(e.target.value)}
                  />
                  {cat}
                </label>
              ))}
            </Box>

            <Typography variant="subtitle1" sx={{ mt: 2 }}>表示対象</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {['全体', '出展者', '協力会社'].map((v) => (
                <label key={v}>
                  <input
                    type="radio"
                    value={v}
                    checked={visibilitySetting === v}
                    onChange={(e) => setVisibilitySetting(e.target.value)}
                  />
                  {v}
                </label>
              ))}
            </Box>

            <TextField
              label="本文"
              multiline
              rows={5}
              fullWidth
              value={noticeBody}
              onChange={(e) => setNoticeBody(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box className="flex">
              <Button
                variant="outlined"
                component="label"
              >
                ファイルを選択
                <input type="file" hidden accept={allowedNoticeAttachmentTypes} onChange={handleFileChange} />
              </Button>

              {file && <Typography variant="body2">選択ファイル: {file.name}</Typography>}
              <Typography variant="body2">※ファイルサイズは10MBまでです。</Typography>

              <Button variant="contained" onClick={handleSubmit}>お知らせを投稿</Button>
            </Box>
            <Box sx={{ mt: 6 }}>
              <Typography variant="h6" gutterBottom>投稿済みお知らせ一覧</Typography>
              {notices.map((notice, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #ccc', borderRadius: '8px' }}>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 1.5,
                      py: 0.5,
                      mb: 1,
                      borderRadius: '4px',
                      backgroundColor:
                        notice.category === '緊急' ? '#f44336' :
                        notice.category === '重要' ? '#ff9800' :
                        '#2196f3',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.8rem'
                    }}
                  >
                    {notice.category}
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary">{notice.date}</Typography>
                  <Typography variant="h6">{notice.title}</Typography>
                  <Link to={`/news/${notice.notice_id}`} component={RouterLink} sx={{ mt: 1, display: 'block' }}>
                    続きを読む
                  </Link>
                </Box>
              ))}

              {/* ページネーション */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                {Array.from({ length: Math.ceil(total / noticesPerPage) }).map((_, idx) => (
                  <Button
                    key={idx}
                    onClick={() => handlePageChange(idx + 1)}
                    variant={page === idx + 1 ? 'contained' : 'outlined'}
                    sx={{ mx: 0.5 }}
                  >
                    {idx + 1}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
      <footer>
        <Typography>
          © 2026 THE MID-JAPAN ECONOMIST.
        </Typography>
      </footer>
    </div>
  );
};

export default CreateNotice;